// One column on the catalog board = one product category. The column is itself
// sortable (categories drag horizontally) and is also a drop target for product
// cards (so cards can land in an empty column) wrapping a SortableContext of
// its product cards. The header carries a drag handle, the category name +
// description, and edit / "Add product" affordances. Inactive categories are
// dimmed and badged.
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box, Paper, Typography, IconButton, Button, Chip, Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ProductCard from './ProductCard.jsx';

const CategoryColumn = ({
  category, products, onEditCategory, onAddProduct, onEditProduct,
}) => {
  const {
    setNodeRef: setSortableRef, attributes, listeners, transform, transition, isDragging,
  } = useSortable({ id: `category-${category.id}`, data: { type: 'category', categoryId: category.id } });

  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `dropzone-${category.id}`,
    data: { type: 'category', categoryId: category.id },
  });

  // When using a DragOverlay, the floating preview follows the cursor —
  // the source column should stay anchored in its current array slot so the
  // dashed placeholder reads as "this is where it'll land". Suppress the
  // sortable transform for the active item; other items still animate aside
  // via SortableContext + horizontalListSortingStrategy.
  const style = {
    transform: isDragging ? undefined : CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
  };

  return (
    <Paper
      ref={setSortableRef}
      style={style}
      variant="outlined"
      sx={{
        width: 300,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '100%',
        bgcolor: 'background.default',
        ...(isDragging && {
          borderStyle: 'dashed',
          borderColor: 'primary.main',
          borderWidth: 2,
          bgcolor: 'action.hover',
          '& > *': { visibility: 'hidden' },
        }),
      }}
    >
      <Box sx={{
        p: 1.5, borderBottom: 1, borderColor: 'divider', opacity: category.is_active ? 1 : 0.6,
      }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            size="small"
            aria-label={`Reorder ${category.name}`}
            sx={{ cursor: 'grab', touchAction: 'none' }}
            {...attributes}
            {...listeners}
          >
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>
            {category.name}
          </Typography>
          {!category.is_active && <Chip label="Inactive" size="small" />}
          <IconButton size="small" aria-label={`Edit ${category.name}`} onClick={() => onEditCategory(category)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
        {category.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              mt: 0.5,
              ml: 4,
            }}
          >
            {category.description}
          </Typography>
        )}
      </Box>

      <Box
        ref={setDroppableRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 1,
          minHeight: 80,
          bgcolor: isOver ? 'action.hover' : 'transparent',
          transition: 'background-color 120ms',
        }}
      >
        <SortableContext
          items={products.map((p) => `product-${p.id}`)}
          strategy={verticalListSortingStrategy}
        >
          <Stack spacing={1}>
            {products.map((product) => (
              <ProductCard key={product.id} product={product} onEdit={onEditProduct} />
            ))}
          </Stack>
        </SortableContext>
        {products.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            No products. Drop one here or add one.
          </Typography>
        )}
      </Box>

      <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
        <Button
          fullWidth
          size="small"
          startIcon={<AddIcon />}
          onClick={() => onAddProduct(category.id)}
        >
          Add product
        </Button>
      </Box>
    </Paper>
  );
};

export default CategoryColumn;
